import type { DisciplineFile } from '../model/interfaces'
import type { ApiResponse } from '@shared/api'

import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'

interface DisciplineFileUploadApiOptions {
  disciplineId: string | number
  packId: number
  file: File
  onProgress?: (percent: number) => void
  onError?: (message: string) => void
}

/** `POST /admin-api/disciplines/{discipline}/files` — one file into the draft pack. Errors stay on the
 *  file row instead of a toast, as the legacy uploader showed them. */
export const useDisciplineFileUploadApi = async (options: DisciplineFileUploadApiOptions): Promise<DisciplineFile | null> => {
  const { $axios } = useNuxtApp()

  const url = useAdminRoutes('admin.disciplines.api.files', { discipline_id: options.disciplineId })

  try {
    if (!url) {
      options.onError?.('Unexpected error. Invalid request URL.')
      return null
    }

    const formData = new FormData()
    formData.append('file', options.file)
    formData.append('pack_id', String(options.packId))

    const { data } = await $axios.post<ApiResponse>(url, formData, {
      onUploadProgress: (event) => {
        if (!event.total) return
        options.onProgress?.(Math.round((event.loaded / event.total) * 100))
      },
    })

    return data.data ?? null
  } catch (err: unknown) {
    options.onError?.(getAxiosErrorMessage(err) || 'Unexpected upload error.')
    return null
  }
}
